import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, Smartphone } from 'lucide-react';

const IOSCompatibilityCheck = () => {
  const [iosInfo, setIosInfo] = useState(null);
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    const checkIOSCompatibility = () => {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
      const hasServiceWorker = 'serviceWorker' in navigator;
      const hasPushNotifications = 'PushManager' in window;
      const hasGeolocation = 'geolocation' in navigator;
      const hasLocalStorage = typeof Storage !== 'undefined';
      const hasTouchSupport = 'ontouchstart' in window;
      const hasOrientation = 'orientation' in window;
      
      // Get iOS version
      let iosVersion = null;
      if (isIOS) {
        const match = navigator.userAgent.match(/OS (\d+)_(\d+)_?(\d+)?/);
        if (match) {
          iosVersion = `${match[1]}.${match[2]}${match[3] ? '.' + match[3] : ''}`;
        }
      }

      // Get screen info
      const screenInfo = {
        width: window.screen.width,
        height: window.screen.height,
        pixelRatio: window.devicePixelRatio,
        orientation: window.screen.orientation?.type || 'unknown'
      };

      // Get safe area insets
      const safeAreaInsets = {
        top: getComputedStyle(document.documentElement).getPropertyValue('--safe-area-top'),
        bottom: getComputedStyle(document.documentElement).getPropertyValue('--safe-area-bottom'),
        left: getComputedStyle(document.documentElement).getPropertyValue('--safe-area-left'),
        right: getComputedStyle(document.documentElement).getPropertyValue('--safe-area-right')
      };

      setIosInfo({
        isIOS,
        iosVersion,
        isStandalone,
        hasServiceWorker,
        hasPushNotifications,
        hasGeolocation,
        hasLocalStorage,
        hasTouchSupport,
        hasOrientation,
        screenInfo,
        safeAreaInsets,
        userAgent: navigator.userAgent
      });
    };

    checkIOSCompatibility();
  }, []);

  if (!iosInfo || !iosInfo.isIOS) {
    return null; // Only show on iOS devices
  }

  const FeatureCheck = ({ feature, label }) => (
    <div className="flex items-center gap-2 py-1">
      {feature ? (
        <CheckCircle className="w-4 h-4 text-green-400" />
      ) : (
        <XCircle className="w-4 h-4 text-red-400" />
      )}
      <span className="text-sm text-gray-300">{label}</span>
    </div>
  );

  return (
    <>
      {/* Floating debug button (only visible in dev mode) */}
      {process.env.NODE_ENV === 'development' && (
        <button
          onClick={() => setShowInfo(!showInfo)}
          className="fixed top-4 right-4 z-50 p-2 bg-cyan-500/20 border border-cyan-500/50 rounded-full hover:bg-cyan-500/30 transition-all"
          title="iOS Compatibility Info"
        >
          <Smartphone className="w-5 h-5 text-cyan-400" />
        </button>
      )}

      {/* iOS Info Modal */}
      {showInfo && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-slate-900 to-blue-900 border-b border-cyan-500/30 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-6 h-6 text-cyan-400" />
                <h2 className="text-xl font-bold text-white">iOS Compatibility Check</h2>
              </div>
              <button
                onClick={() => setShowInfo(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Device Info */}
              <div className="bg-slate-800/50 rounded-lg p-4">
                <h3 className="text-lg font-bold text-cyan-400 mb-2">Device Information</h3>
                <div className="space-y-1 text-sm">
                  <p className="text-gray-300"><span className="font-semibold">iOS Version:</span> {iosInfo.iosVersion || 'Unknown'}</p>
                  <p className="text-gray-300"><span className="font-semibold">Screen:</span> {iosInfo.screenInfo.width}x{iosInfo.screenInfo.height}</p>
                  <p className="text-gray-300"><span className="font-semibold">Pixel Ratio:</span> {iosInfo.screenInfo.pixelRatio}x</p>
                  <p className="text-gray-300"><span className="font-semibold">Orientation:</span> {iosInfo.screenInfo.orientation}</p>
                  <p className="text-gray-300"><span className="font-semibold">Standalone Mode:</span> {iosInfo.isStandalone ? 'Yes (Installed)' : 'No (Browser)'}</p>
                </div>
              </div>

              {/* Feature Support */}
              <div className="bg-slate-800/50 rounded-lg p-4">
                <h3 className="text-lg font-bold text-cyan-400 mb-2">Feature Support</h3>
                <div className="grid grid-cols-2 gap-2">
                  <FeatureCheck feature={iosInfo.hasServiceWorker} label="Service Worker" />
                  <FeatureCheck feature={iosInfo.hasPushNotifications} label="Push Notifications" />
                  <FeatureCheck feature={iosInfo.hasGeolocation} label="Geolocation" />
                  <FeatureCheck feature={iosInfo.hasLocalStorage} label="Local Storage" />
                  <FeatureCheck feature={iosInfo.hasTouchSupport} label="Touch Events" />
                  <FeatureCheck feature={iosInfo.hasOrientation} label="Orientation API" />
                </div>
              </div>

              {/* Safe Area Insets */}
              <div className="bg-slate-800/50 rounded-lg p-4">
                <h3 className="text-lg font-bold text-cyan-400 mb-2">Safe Area Insets</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <p className="text-gray-300"><span className="font-semibold">Top:</span> {iosInfo.safeAreaInsets.top || '0px'}</p>
                  <p className="text-gray-300"><span className="font-semibold">Bottom:</span> {iosInfo.safeAreaInsets.bottom || '0px'}</p>
                  <p className="text-gray-300"><span className="font-semibold">Left:</span> {iosInfo.safeAreaInsets.left || '0px'}</p>
                  <p className="text-gray-300"><span className="font-semibold">Right:</span> {iosInfo.safeAreaInsets.right || '0px'}</p>
                </div>
              </div>

              {/* Installation Instructions */}
              {!iosInfo.isStandalone && (
                <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                  <h3 className="text-lg font-bold text-blue-400 mb-2">How to Install on iOS</h3>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-blue-300">
                    <li>Tap the Share button (square with arrow) in Safari</li>
                    <li>Scroll down and tap "Add to Home Screen"</li>
                    <li>Customize the name if desired</li>
                    <li>Tap "Add" in the top right corner</li>
                  </ol>
                </div>
              )}

              {/* User Agent */}
              <div className="bg-slate-800/50 rounded-lg p-4">
                <h3 className="text-lg font-bold text-cyan-400 mb-2">User Agent</h3>
                <p className="text-xs text-gray-400 break-all">{iosInfo.userAgent}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default IOSCompatibilityCheck;
